<template>
  <b-card>
    <b-card-title class="mb-1">
      <feather-icon
        icon="BarChartIcon"
        size="24"
        class="mr-1"
      />
      Benutzer-Statistiken
    </b-card-title>

    <b-card-body>
      <!-- Statistics Grid -->
      <b-row class="match-height">
        <!-- Total Items Created -->
        <b-col
          cols="6"
          md="3"
          class="mb-2"
        >
          <div class="statistic-item text-center p-2 border rounded">
            <feather-icon
              icon="PlusCircleIcon"
              size="24"
              class="text-primary mb-1"
            />
            <h4 class="mb-0 text-primary">
              {{ statistics.totalCreated }}
            </h4>
            <small class="text-muted">Erstellte Einträge</small>
          </div>
        </b-col>

        <!-- Active Sessions -->
        <b-col
          cols="6"
          md="3"
          class="mb-2"
        >
          <div class="statistic-item text-center p-2 border rounded">
            <feather-icon
              icon="ActivityIcon"
              size="24"
              class="text-success mb-1"
            />
            <h4 class="mb-0 text-success">
              {{ statistics.activeSessions }}
            </h4>
            <small class="text-muted">Aktive Sessions</small>
          </div>
        </b-col>

        <!-- Last Login -->
        <b-col
          cols="6"
          md="3"
          class="mb-2"
        >
          <div class="statistic-item text-center p-2 border rounded">
            <feather-icon
              icon="ClockIcon"
              size="24"
              class="text-warning mb-1"
            />
            <h6 class="mb-0 text-warning small">
              {{ lastLoginDisplay }}
            </h6>
            <small class="text-muted">Letzter Login</small>
          </div>
        </b-col>

        <!-- Account Age -->
        <b-col
          cols="6"
          md="3"
          class="mb-2"
        >
          <div class="statistic-item text-center p-2 border rounded">
            <feather-icon
              icon="CalendarIcon"
              size="24"
              class="text-info mb-1"
            />
            <h6 class="mb-0 text-info">
              {{ accountAge }}
            </h6>
            <small class="text-muted">Kontodauer</small>
          </div>
        </b-col>
      </b-row>

      <!-- Detailed Activity Breakdown -->
      <div class="mt-3">
        <h6 class="mb-2">
          <feather-icon
            icon="PieChartIcon"
            size="16"
            class="mr-1"
          />
          Aktivitäts-Aufschlüsselung
        </h6>

        <div class="activity-breakdown">
          <!-- Customers Created -->
          <div class="activity-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="UsersIcon"
                size="16"
                class="text-primary mr-2"
              />
              <span>Erstellte Kunden</span>
            </div>
            <b-badge variant="primary">
              {{ statistics.customersCreated }}
            </b-badge>
          </div>

          <!-- Orders Processed -->
          <div class="activity-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="ShoppingCartIcon"
                size="16"
                class="text-success mr-2"
              />
              <span>Bearbeitete Buchungen</span>
            </div>
            <b-badge variant="success">
              {{ statistics.ordersProcessed }}
            </b-badge>
          </div>

          <!-- Offers Created -->
          <div class="activity-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="FileTextIcon"
                size="16"
                class="text-warning mr-2"
              />
              <span>Erstellte Angebote</span>
            </div>
            <b-badge variant="warning">
              {{ statistics.offersCreated }}
            </b-badge>
          </div>

          <!-- Inquiries Handled -->
          <div class="activity-item d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="MessageSquareIcon"
                size="16"
                class="text-info mr-2"
              />
              <span>Bearbeitete Anfragen</span>
            </div>
            <b-badge variant="info">
              {{ statistics.inquiriesHandled }}
            </b-badge>
          </div>
        </div>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BBadge,
} from 'bootstrap-vue'
import helper from '@/helper/helper'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BBadge,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { getFormattedDate } = helper()
    return { getFormattedDate }
  },
  data() {
    return {
      statistics: {
        totalCreated: 0,
        activeSessions: 0,
        customersCreated: 0,
        ordersProcessed: 0,
        offersCreated: 0,
        inquiriesHandled: 0,
        averageDailyActivity: 0,
        productivityScore: 0,
        errorRate: 0,
      },
    }
  },
  computed: {
    lastLoginDisplay() {
      if (!this.user.lastLogin && !this.user.updated_at) return 'Nie'

      const loginDate = this.user.lastLogin || this.user.updated_at
      const now = new Date()
      const login = new Date(loginDate)
      const diffInDays = Math.floor((now - login) / (1000 * 60 * 60 * 24))

      if (diffInDays === 0) return 'Heute'
      if (diffInDays === 1) return 'Gestern'
      if (diffInDays < 7) return `vor ${diffInDays} Tagen`
      if (diffInDays < 30) return `vor ${Math.floor(diffInDays / 7)} Wochen`

      return `vor ${Math.floor(diffInDays / 30)} Monaten`
    },
    accountAge() {
      if (!this.user.created_at) return 'Unbekannt'

      const now = new Date()
      const created = new Date(this.user.created_at)
      const diffInDays = Math.floor((now - created) / (1000 * 60 * 60 * 24))

      if (diffInDays < 1) return 'Heute erstellt'
      if (diffInDays < 7) return `${diffInDays} Tage`
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} Wochen`
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} Monate`

      return `${Math.floor(diffInDays / 365)} Jahre`
    },
  },
  mounted() {
    this.generateStatistics()
  },
  methods: {
    generateStatistics() {
      // Generate mock statistics based on user data
      // In a real application, this would fetch from an API

      const daysSinceCreation = this.user.created_at
        ? Math.floor((new Date() - new Date(this.user.created_at)) / (1000 * 60 * 60 * 24))
        : 1

      // Generate realistic mock data
      const baseActivity = Math.floor(Math.random() * 50) + 10

      this.statistics = {
        totalCreated: baseActivity + Math.floor(Math.random() * 100),
        activeSessions: Math.floor(Math.random() * 3) + 1,
        customersCreated: Math.floor(baseActivity * 0.3),
        ordersProcessed: Math.floor(baseActivity * 0.4),
        offersCreated: Math.floor(baseActivity * 0.2),
        inquiriesHandled: Math.floor(baseActivity * 0.1),
        averageDailyActivity: Math.floor(baseActivity / Math.max(daysSinceCreation, 1)),
        productivityScore: Math.min(95, Math.floor(Math.random() * 40) + 55),
        errorRate: Math.floor(Math.random() * 10) + 1,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.statistic-item {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.activity-breakdown .activity-item {
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e3f2fd !important;
  }
}

.progress {
  background-color: #f1f1f1;
  border-radius: 10px;
}

.progress-bar {
  border-radius: 10px;
  transition: width 0.3s ease;
}

.border-top {
  border-top: 1px solid #ebe9f1 !important;
}

.metric-row {
  padding: 0.25rem 0;
}

</style>
